import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import ScrollToTop from 'react-scroll-to-top'

import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp'
import {Box, Grid, Theme} from '@mui/material'
import {PGButton, PGChip, PGTypography} from '@progynyglobal/pg-ui-library'
import HTMLReactParser, {attributesToProps, domToReact} from 'html-react-parser'
import readingTime from 'reading-time'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as EyeIcon} from '@progyny/assets/images/eye.svg'
import BackButton from '@progyny/components/Buttons/BackButton'
import PageLoader from '@progyny/components/PageLoader'
import QuestionBanner from '@progyny/pages/KnowledgeCenter/Article/Details/TopicDetails/QuestionBanner'
import RelatedArticles from '@progyny/pages/KnowledgeCenter/Article/Details/TopicDetails/RelatedArticles'
import RelatedCategories from '@progyny/pages/KnowledgeCenter/Article/Details/TopicDetails/RelatedCategories'

import {theme} from '@root/theme'

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(15),
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      marginTop: 100,
    },
  },
  topImage: {
    objectFit: 'cover',
    width: '100%',
    height: 200,
  },
  gridItem: {
    paddingRight: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  relatedArticles: {
    padding: 32,
    backgroundColor: theme.palette.other.brandColors.white,
    boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.12)',
    borderRadius: 16,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      boxShadow: 'none',
      backgroundColor: '#F9F9FB',
    },
  },
  title: {
    fontSize: theme.typography.h2.fontSize,
    lineHeight: theme.typography.h2.lineHeight,
    letterSpacing: theme.typography.subtitle1.letterSpacing,
    fontWeight: theme.typography.subtitle1.fontWeight,
    marginTop: theme.spacing(5),
  },
  htmlWrapper: {
    width: 720,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      overflowWrap: 'anywhere',
    },
    '&>*:first-child': {
      paddingTop: 0,
      marginTop: 0,
    },
    '& h1': {
      fontSize: 26,
      fontWeight: 600, // semi-bold
      lineHeight: '32px',
      letterSpacing: 0.15,
      marginBottom: 0,
      marginTop: theme.spacing(6),
    },
    '& h2': {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 600, // semi-bold
      lineHeight: '20px',
      letterSpacing: 0.15,
      marginBottom: 0,
      marginTop: theme.spacing(5),
    },
    '& code': {
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.disabled,
      paddingTop: theme.spacing(1),
      display: 'block',
    },
    '& em': {
      fontSize: theme.typography.caption.fontSize,
    },
    '& p': {
      fontSize: theme.typography.body1.fontSize,
      lineHeight: '24px',
      margin: `${theme.spacing(2)} 0`,
      [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
      },
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecorationLine: 'underline',
      [theme.breakpoints.down('sm')]: {
        overflowWrap: 'anywhere',
      },
    },
    '& a:hover': {
      color: theme.palette.primary.dark,
    },
    '& ol': {
      fontSize: theme.typography.body1.fontSize,
      lineHeight: '24px',
    },
    '& ol > li:not(:first-child)': {
      marginTop: theme.spacing(0.75),
    },
    '& ul': {
      fontSize: theme.typography.body1.fontSize,
      margin: `${theme.spacing(3)} 0`,
      lineHeight: '24px',
    },
    '& ul > li:not(:first-child)': {
      marginTop: theme.spacing(0.75),
    },
    '& .references ol': {
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 'initial',
    },
    '& .quote': {
      padding: `${theme.spacing(0.5)} ${theme.spacing(3)}`,
      background: theme.palette.other.brandColors.seashell,
    },
    '& .space': {
      padding: theme.spacing(1),
    },
    '& img': {
      height: 'auto',
      width: 'auto',
      display: 'block',
      margin: '0 auto',
      [theme.breakpoints.down('md')]: {
        height: '100%',
        width: '100%',
      },
    },
    '& table': {
      margin: `${theme.spacing(4)} auto`,
      borderSpacing: 0,
      borderCollapse: 'collapse',
      '& img': {
        [theme.breakpoints.down('md')]: {
          height: 'auto',
          width: 'auto',
        },
      },
      '& .text-center': {
        '& p': {
          textAlign: 'center',
        },
      },
      minWidth: 600,
      '& p': {
        fontSize: theme.typography.body2.fontSize,
        margin: 0,
      },
      '& th': {
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
        height: 58,
        color: 'white',
        textAlign: 'left',
        background: theme.palette.other.brandColors.primary,
        borderLeft: `1px solid ${theme.palette.other.brandColors.primary}`,
        borderRight: `1px solid ${theme.palette.other.brandColors.primary}`,
      },
      '& tbody': {
        borderLeft: `1px solid ${theme.palette.secondary.muted}`,
        borderRight: `1px solid ${theme.palette.secondary.muted}`,
        '& tr': {
          height: 58,
          borderBottom: `1px solid ${theme.palette.secondary.muted}`,
          '& td': {
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            '& strong': {
              [theme.breakpoints.up('sm')]: {
                whiteSpace: 'nowrap',
              },
            },
          },
        },
      },
    },
    marginBottom: theme.spacing(6),
  },
  featuredChip: {
    borderRadius: 32,
    width: 'fit-content',
    marginTop: 16,
    marginBottom: 16,
  },
}))

type Article = {
  squareThumbnailUrl: string
  slug: string
  title: string
}

type ArticleCategory = {
  imageUrl: string
  slug: string
  name: string
}
interface TopicDetailsProps {
  onBackClick: () => void
  title?: string | null
  imageUrl?: string
  loading?: boolean
  header?: React.ReactNode
  titleHeader?: React.ReactNode
  html?: string
  relatedTopics?: Article[]
  relatedSegments?: ArticleCategory[]
  isFeatured?: boolean
}

const TopicDetails: FC<TopicDetailsProps> = ({
  onBackClick,
  title,
  loading,
  header,
  html,
  relatedTopics,
  relatedSegments,
  titleHeader,
  isFeatured,
}: TopicDetailsProps) => {
  const {t} = useTranslation()
  const {classes} = useStyles()
  const {search} = useLocation()
  const queryParams = new URLSearchParams(search)
  const treatment = queryParams.get('treatment')
  const {minutes} = readingTime(html || '', {wordsPerMinute: 300})

  if (loading) return <PageLoader position={'center'} />

  return (
    <Box className={classes.container}>
      {onBackClick && (
        <Box
          sx={{
            borderBottom: {xs: '1px solid #E5E7EB', md: 'none'},
            pb: {xs: 1.5, md: 0},
            mb: {xs: 3, md: 0},
          }}
        >
          <BackButton text={treatment!} onBackClick={onBackClick} />
        </Box>
      )}
      <PGTypography
        className={classes.title}
        variant='header'
        sx={{width: {xs: '100%', md: 720}}}
      >
        {title}
      </PGTypography>
      {isFeatured && (
        <PGChip
          label={t('knowledge_center_article_details_topic_details_featured')}
          className={classes.featuredChip}
        />
      )}
      <PGTypography
        variant='body'
        mt={1}
        color={theme.palette.other.brandColors.lightGray}
        sx={{display: 'flex', alignItems: 'center', gap: '4px'}}
      >
        <EyeIcon />
        {minutes} {t('knowledge_center_article_details_topic_details_min_read')}
      </PGTypography>
      <Grid
        container
        mt={5}
        sm={12}
        md={12}
        lg={12}
        justifyContent={'space-between'}
      >
        <Grid item xs={12} sm={8} md={8} lg={7}>
          <Box
            sx={{
              height: {xs: 189, md: 195},
              width: {xs: '100%', md: 720},
              overflow: 'hidden',
              display: {xs: 'flex', md: 'block'},
              justifyContent: {xs: 'center', md: 'initial'},
            }}
            mb={5}
          >
            {header}
          </Box>
          {titleHeader}
          <Box className={classes.htmlWrapper}>
            {html &&
              HTMLReactParser(html, {
                replace(node: any) {
                  if (node.type === 'tag' && node.name === 'table') {
                    return (
                      <Box
                        sx={{
                          overflowX: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          width: '100%',
                        }}
                      >
                        <table {...attributesToProps(node.attribs)}>
                          {domToReact(node.children)}
                        </table>
                      </Box>
                    )
                  }
                },
              })}
          </Box>
          <QuestionBanner />
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={4}>
          {!!relatedTopics?.length && (
            <Box className={classes.relatedArticles}>
              <RelatedArticles
                title={t(
                  'knowledge_center_article_details_topic_details_related_articles'
                )}
                topics={relatedTopics}
              />
            </Box>
          )}
          {!!relatedSegments?.length && (
            <Box mt={6} className={classes.relatedArticles}>
              <RelatedCategories
                title={t(
                  'knowledge_center_article_details_topic_details_you_might_also_be_inter'
                )}
                segments={relatedSegments}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        top={1200}
        component={
          <PGButton
            variant='outlined'
            endIcon={<ArrowUpwardSharpIcon />}
            label={t(
              'knowledge_center_article_details_topic_details_scroll_to_top'
            )}
          />
        }
      />
    </Box>
  )
}

export default TopicDetails
